import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatError } from "@/lib/api";

const FIELDS = [["rate_in_per_m", "Input cost (USD / 1M tokens)"], ["rate_out_per_m", "Output cost (USD / 1M tokens)"],
  ["alert_pct", "Alert admin at % of a user's cap"], ["daily_total_alert_usd", "Daily total alert (USD)"]];

export function AdminAiCosts({ data, onSaved }) {
  const [f, setF] = useState(data.ai_cost);
  const [packs, setPacks] = useState(data.topups);
  const setPack = (i, k, v) => setPacks(packs.map((p, j) => (j === i ? { ...p, [k]: v } : p)));
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/ai-costs").then((r) => setStats(r.data)); }, []);
  const save = async () => {
    try {
      await api.put("/admin/billing", { notice: data.notice, payments_enabled: data.payments.enabled, trial_days: data.trial_days,
        ai_cost: Object.fromEntries(FIELDS.map(([k]) => [k, Number(f[k])])),
        topups: packs.map((p) => ({ id: p.id, requests: Number(p.requests), price_inr: Number(p.price_inr), price_usd: Number(p.price_usd) })) });
      toast.success("AI cost settings saved"); onSaved();
    } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div data-testid="admin-ai-costs" className="grid gap-4 rounded-xl border border-line bg-surface p-6">
      <h3 className="font-bold text-ink">AI cost tracking & alerts</h3>
      <p className="text-sm text-ink-2">Costs are estimated from text length (about 4 characters per token). Each plan has a hard monthly or daily cost cap (set per plan above). Alerts are emailed to the admin address.</p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {FIELDS.map(([k, l]) => <div key={k} className="flex flex-col gap-1.5"><Label className="text-xs">{l}</Label><Input type="number" min={0} step="0.1" data-testid={`ai-cost-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></div>)}
      </div>
      <div data-testid="admin-topups" className="grid gap-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-ink-3">AI request top-up packs (Plus & Pro, valid 90 days)</p>
        {packs.map((p, i) => (
          <div key={p.id} className="grid grid-cols-3 gap-3 sm:max-w-xl">
            {[["requests", "Requests"], ["price_inr", "Price (₹)"], ["price_usd", "Price ($)"]].map(([k, l]) => (
              <div key={k} className="flex flex-col gap-1"><Label className="text-xs">{l}</Label>
                <Input type="number" min={1} data-testid={`topup-${p.id}-${k}`} value={p[k]} onChange={(e) => setPack(i, k, e.target.value)} /></div>
            ))}
          </div>
        ))}
      </div>
      <Button data-testid="ai-cost-save-btn" onClick={save} className="justify-self-start rounded-full">Save AI cost & top-up settings</Button>
      {stats && (
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="overflow-x-auto lg:col-span-2">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-3">Top users, last 30 days</p>
            <table className="w-full text-sm"><thead className="bg-canvas-2 text-left text-xs text-ink-3"><tr><th className="p-2">User</th><th className="p-2">Plan</th><th className="p-2">AI requests</th><th className="p-2">Est. cost</th></tr></thead>
              <tbody>{stats.top_users.map((u) => <tr key={u.user_id} data-testid={`ai-cost-user-${u.email}`} className="border-t border-line"><td className="p-2">{u.email}</td><td className="p-2 capitalize">{u.plan}</td><td className="p-2 font-mono">{u.requests}</td><td className="p-2 font-mono">${u.cost_usd.toFixed(3)}</td></tr>)}
                {stats.top_users.length === 0 && <tr><td colSpan={4} className="p-2 text-ink-3">No AI usage yet.</td></tr>}</tbody></table>
          </div>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-3">Daily totals</p>
            <ul data-testid="ai-cost-days" className="text-sm">{stats.days.map((d) => <li key={d.date} className="flex justify-between border-t border-line py-1.5"><span>{d.date}</span><span className="font-mono">${d.cost_usd.toFixed(3)}</span></li>)}</ul>
          </div>
        </div>
      )}
    </div>
  );
}
